import {
  createCoreAxiosInstance,
  createMenuManagementAxiosInstance,
  createOrchestratorAxiosInstance,
} from 'createAxiosInstance';
import * as Sentry from '@sentry/react';

import { CUBOH } from 'constants/integrations';
import { checkIntegrationIsActive } from 'helpers/integrations';
import {
  CUBOH_MENU_CHILD_MAP,
  MENU_OBJECT_TYPES,
  OLDEST_POSSIBLE_PUBLISH_DATE_IN_MILLISECONDS,
  SPECIAL_INSTRUCTIONS,
  SECONDARY_PRICES,
  SKIP_ONLY_TAXES,
  TYPE_TO_READABLE_STRING_MAP,
  TYPE_TO_READABLE_STRING_PLURAL,
  classificationFallbackShortLabel,
} from 'constants/menuManagement';
import { IMAGE_CONTENT_REQUEST_TYPE } from 'constants/cuboh';
import { REQUEST_TYPE } from 'constants/api';

import { axiosApiRequest } from 'helpers/api';
import { getMasterMenuPermissionTier } from 'helpers/masterMenuPermissions';

export const replaceTypeWithReadableInString = str =>
  Object.keys(TYPE_TO_READABLE_STRING_MAP).reduce((messageText, key) => {
    const exp = new RegExp(key, 'g');
    return messageText.replace(exp, TYPE_TO_READABLE_STRING_MAP[key]);
  }, str);

export const getReadableItemTypePlural = itemType =>
  (TYPE_TO_READABLE_STRING_PLURAL[itemType] ?? '').toLowerCase();

export const fetchMerchantAppIntegrations = async merchantId => {
  const axiosInstance = await createCoreAxiosInstance();
  const { data } = await axiosInstance.get(`api/v2/integrations?merchantID=${merchantId}`);
  return data?.applications;
};

export const getSimpleMenuByMerchant = async (merchantID, companyID) => {
  const axios = await createMenuManagementAxiosInstance();
  const { data } = await axios.get(
    `cuboh/company/${companyID}/merchant/${merchantID}/menus/simple/all`,
  );
  return data;
};

export const getActiveAppIntegrationsByMerchant = async (
  merchantID,
  addCubohAsIntegration = false,
) => {
  const allMerchantIntegrations = await fetchMerchantAppIntegrations(merchantID);

  return Object.fromEntries(
    Object.entries(allMerchantIntegrations).filter(
      ([key]) =>
        checkIntegrationIsActive(allMerchantIntegrations[key]) ||
        allMerchantIntegrations[key].activeData?.official_onboarding ||
        (key === CUBOH.toLowerCase() && addCubohAsIntegration),
    ),
  );
};

export const addIntegrationToMenus = async ({
  companyId,
  merchantId,
  integrationName,
  menuUuids,
}) => {
  const axios = await createMenuManagementAxiosInstance();
  return axios.post(`cuboh/company/${companyId}/merchant/${merchantId}/menus/integration/assign`, {
    integrationName,
    menuUuids,
  });
};

export const normalizeCubohMenus = (
  data,
  isParentCompanyUser,
  cubohUser = null,
  companies = null,
  singleSelectedCompanyID = null,
  isInternalUser = false,
) =>
  data.reduce((accumulator, currentMenu) => {
    const uniqueId = currentMenu?.parentUuid
      ? `${currentMenu?.uuid}_${currentMenu.parentUuid}`
      : currentMenu?.uuid;
    const merchantInArray = Object.keys(accumulator)?.find(
      merchantId => parseInt(merchantId, 10) === parseInt(currentMenu?.merchantId, 10),
    );

    const masterMenuPermissionTier =
      cubohUser && companies && singleSelectedCompanyID
        ? getMasterMenuPermissionTier({
            cubohUser,
            companies,
            menu: currentMenu,
            singleSelectedCompanyID,
            isInternalUser,
          })
        : null;

    if (!merchantInArray) {
      accumulator[currentMenu?.merchantId] = [
        {
          ...currentMenu,
          key: uniqueId,
          parents: [],
          children: [],
          merchantId: parseInt(currentMenu?.merchantId, 10),
          companyId: parseInt(currentMenu?.companyId, 10),
          notParentUserAndIsMasterMenu:
            (!isParentCompanyUser && currentMenu?.associatedMerchants?.length > 0) ?? false,
          masterMenuPermissionTier,
        },
      ];
    }

    if (parseInt(merchantInArray, 10) === parseInt(currentMenu?.merchantId, 10)) {
      accumulator[currentMenu?.merchantId] = [
        ...accumulator[merchantInArray],
        {
          ...currentMenu,
          key: uniqueId,
          parents: [],
          children: [],
          merchantId: parseInt(currentMenu?.merchantId, 10),
          companyId: parseInt(currentMenu?.companyId, 10),
          notParentUserAndIsMasterMenu:
            (!isParentCompanyUser && currentMenu?.associatedMerchants?.length > 0) ?? false,
          masterMenuPermissionTier,
        },
      ];
    }

    return accumulator;
  }, {});

export const fetchCubohMenus = async ({
  companyID,
  merchantID = 0,
  menuID = false,
  isParentCompanyUser,
  setError,
  cubohUser = null,
  companies = null,
  singleSelectedCompanyID = null,
  isInternalUser = false,
}) => {
  try {
    const menuIDSuffix = menuID ? `/${menuID}` : '';

    const data = await axiosApiRequest({
      createAxiosInstance: createMenuManagementAxiosInstance,
      method: REQUEST_TYPE.GET,
      path: `/company/${companyID}/cuboh/merchant/${merchantID}/menus${menuIDSuffix}`,
      setError,
    });

    const normalizedData = normalizeCubohMenus(
      data?.items ?? [],
      isParentCompanyUser,
      cubohUser,
      companies,
      singleSelectedCompanyID,
      isInternalUser,
    );

    return {
      ...normalizedData,
      error: null,
    };
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        companyID,
        merchantID,
        menuID,
      },
    });
    return {
      error: error.message || 'An error occurred while fetching menus, please try again.',
    };
  }
};

export const normalizeCubohMenuItems = data =>
  data.reduce((accumulator, currentItem) => {
    const uniqueId = currentItem.parentUuid
      ? `${currentItem.uuid}_${currentItem.parentUuid}`
      : currentItem.uuid;
    accumulator[uniqueId] = {
      key: uniqueId,
      parents: [],
      children: [],
      ...currentItem,
      merchantId: parseInt(currentItem.merchantId, 10),
      companyId: parseInt(currentItem.companyId, 10),
    };
    return accumulator;
  }, {});

export const fetchCubohItemChildren = async ({
  companyID,
  merchantID,
  parentUuid,
  searchUuid = '',
}) => {
  try {
    const axios = await createMenuManagementAxiosInstance();
    const path = `/company/${companyID}/cuboh/merchant/${merchantID}/children/${parentUuid}${
      searchUuid ? `/${searchUuid}` : ''
    }`;
    const response = await axios.get(path);
    return response.data;
  } catch (e) {
    Sentry.captureException(e, { extra: { companyID, merchantID, parentUuid, searchUuid } });
    // Re-throw rather than swallow: callers await this and immediately read
    // .itemType/.items off the result, so returning undefined here just moves
    // the crash to every call site instead of preventing it.
    throw e;
  }
};

export const patchCubohMenus = async ({ companyID, merchantID, itemID, items }) => {
  const axios = await createMenuManagementAxiosInstance();
  const updatedItemResponse = await axios.patch(
    `/company/${companyID}/cuboh/merchant/${merchantID}/menus/${itemID}/item`,
    { items },
  );
  return updatedItemResponse.data;
};

export const patchMerchantMenuOrdinals = async ({ companyID, merchantID, items }) => {
  const axios = await createMenuManagementAxiosInstance();
  const updatedItemResponse = await axios.patch(
    `/company/${companyID}/cuboh/merchant/${merchantID}/ordinal`,
    { items },
  );
  return updatedItemResponse.data;
};

export const createNewCubohItemHelper = async ({ companyID, merchantID, items }) => {
  const axios = await createMenuManagementAxiosInstance();
  const response = await axios.post(`/company/${companyID}/cuboh/merchant/${merchantID}/item`, {
    items,
  });
  return response?.data;
};

export const fetchItemsByType = async ({ companyId, merchantId, menuId, itemType }) => {
  const axios = await createMenuManagementAxiosInstance();
  const response = await axios.get(
    `/company/${companyId}/cuboh/merchant/${merchantId}/menu/${menuId}/type/${itemType}`,
  );
  return response.data?.items;
};

export const duplicateCubohMenuInMenuManagement = async ({
  companyId,
  merchantId,
  menuId,
  targetMerchants,
  newMenuName,
}) => {
  const axios = await createMenuManagementAxiosInstance();
  const { data } = await axios.post(
    `company/${companyId}/cuboh/merchant/${merchantId}/menus/${menuId}/duplicate`,
    { targetMerchants, newMenuName },
  );
  return data;
};

export const fetchSupportedIntegrations = async ({ companyID }) => {
  const axios = await createMenuManagementAxiosInstance();
  const response = await axios.get(`/company/${companyID}/cuboh/integrations/supported`);
  return response.data?.supportedIntegrations;
};

export const fetchClassifications = async ({ companyID }) => {
  const axios = await createMenuManagementAxiosInstance();
  const response = await axios.get(`/company/${companyID}/cuboh/classifications`);
  return response.data;
};

export const fetchResourcePOS = async companyID => {
  const axios = await createCoreAxiosInstance();
  try {
    const { status, data: response } = await axios.get(`/api/v2/pos-resource/${companyID}`);
    if (status === 200) {
      return response.resource;
    }
    return null;
  } catch {
    return null;
  }
};

export const triggerCacheIfNeeded = async ({ companyId, merchantId, menuId }) => {
  const axiosInstance = await createMenuManagementAxiosInstance();
  return axiosInstance.post(
    `/company/${companyId}/cuboh/merchant/${merchantId}/menus/detailed/full/${menuId}`,
  );
};

export const getIntegrationsByStatus = async (companyId, merchantId, status) => {
  const axios = await createMenuManagementAxiosInstance();
  return axios.get(`company/${companyId}/merchant/${merchantId}/status/${status}`);
};

export const importMenu = async ({ merchantId, companyId, source }) => {
  const axios = await createOrchestratorAxiosInstance();
  return axios.post('/mm/pull', {
    company_id: companyId,
    merchant_id: merchantId,
    source,
  });
};

export const importMenuViaMenuManagement = async ({ companyId, merchantId, source }) => {
  const axios = await createMenuManagementAxiosInstance();
  return axios.post(`/company/${companyId}/merchant/${merchantId}/menu/pull`, {
    source,
  });
};

export const syncReadOnlyMenu = async ({ companyId, merchantId, source }) => {
  const axios = await createMenuManagementAxiosInstance();
  return axios.post(`company/${companyId}/cuboh/merchant/${merchantId}/sync/menu`, {
    pull_integration: source.app,
    integration_id: source.id,
  });
};

export const syncReadOnlyMenuViaOrchestrator = async ({ companyId }) => {
  const axios = await createOrchestratorAxiosInstance();
  await axios.post('/pos', {
    company_id: companyId,
  });
};

export const publishMenus = async ({ menu, data }) => {
  const axios = await createMenuManagementAxiosInstance();
  const response = await axios.post(
    `v2/company/${menu.companyId}/merchant/${menu.merchantId}/menu/push`,
    data,
  );
  return response.data;
};

export const getReadableAppName = (applicationName, supportedApplications) => {
  if (!supportedApplications) return applicationName;
  const application = supportedApplications.find(
    app => app.integrationName === applicationName && (app.isSupportedPull || app.isSupportedPush),
  );
  return application?.readableAppName ?? applicationName;
};

export const getPublishData = async menu => {
  const axios = await createMenuManagementAxiosInstance();
  return axios.get(
    `v2/company/${menu.companyId}/merchant/${menu.merchantId}/menu/${menu.uuid}/push/build`,
  );
};

export const validateMenu = async ({ companyId, merchantId, menuId, isPublish }) => {
  const axios = await createMenuManagementAxiosInstance();
  const { data } = await axios.post(
    `/company/${companyId}/merchant/${merchantId}/menu/${menuId}/validation`,
    { validationLevel: 'db', isPublish },
  );

  return data;
};

export const runMenuFixer = async ({ companyId, merchantId, uuid }) => {
  const axios = await createMenuManagementAxiosInstance();
  return axios.patch(`company/${companyId}/cuboh/merchant/${merchantId}/menu/fix`, {
    menuUuid: uuid,
    sendNotification: true,
    fixerFunction: 'convert_images',
  });
};

/**
 * Because Python stores time in seconds, we need to convert to milliseconds
 *
 * @param timeInSeconds the time since epoch in seconds
 *
 * @returns time since epoch in milliseconds or oldest possible publish date if date is too old
 */
export const formatIntegrationPublishDateToMilliseconds = timeInSeconds => {
  const timeInMilliseconds = timeInSeconds * 1000;
  if (timeInMilliseconds < OLDEST_POSSIBLE_PUBLISH_DATE_IN_MILLISECONDS) {
    return OLDEST_POSSIBLE_PUBLISH_DATE_IN_MILLISECONDS;
  }
  return timeInMilliseconds;
};

export const generateTooltip = ({ title, dataIndex }) => {
  if (dataIndex === SPECIAL_INSTRUCTIONS.key) {
    return `Enable Special Instructions for Orders, only supported by ${Object.values(
      SPECIAL_INSTRUCTIONS.apps,
    ).join(', ')}`;
  }
  if (dataIndex === SECONDARY_PRICES.key) {
    const appList = Object.values(SECONDARY_PRICES.apps).join(', ');
    return `Pick-up Price controls the base/pick-up price for your menus on ${appList}. This field applies only to ${appList}. Other platforms will ignore this setting. If left blank, the default price will be used for both delivery and pick-up.`;
  }
  return title;
};

export const formatNameWithPrice = item => {
  if (item.name && (item.price || item.price === 0)) {
    if (
      item.itemType &&
      ![MENU_OBJECT_TYPES.items, MENU_OBJECT_TYPES.modifiers].includes(item.itemType)
    ) {
      return item.name;
    }
    return `${item.name} - $${(item.price / 100).toFixed(2)}`;
  }
  if (item.name) return item.name;
  return `Invalid item - ${item?.uuid}`;
};

export const copyItemsBetweenMenus = async ({
  targetParentRecord,
  sourceItemList,
  duplicateChildren,
  targetItemType,
}) => {
  const axios = await createMenuManagementAxiosInstance();
  const { data } = await axios.patch(
    `company/${targetParentRecord.companyId}/cuboh/merchant/${targetParentRecord.merchantId}/item/duplicate`,
    {
      sourceItemUuids: sourceItemList,
      targetParentUuid: targetParentRecord.uuid,
      targetItemType,
      duplicateChildren,
    },
  );
  return data;
};

export const getReadOnlySyncData = async (companyId, merchantId) => {
  const axios = await createMenuManagementAxiosInstance();
  return axios.get(`company/${companyId}/merchant/${merchantId}/sync_data`);
};

export const getItemsFromReadOnlyMenuByTargetParent = async ({ record }) => {
  const axios = await createMenuManagementAxiosInstance();
  const { data } = await axios.get(
    `company/${record.companyId}/cuboh/merchant/${
      record.merchantId
    }/read_only/children/${record.uuid}/${CUBOH_MENU_CHILD_MAP[record.itemType]}`,
  );
  return data;
};

export const getItemsFromReadOnlyMenu = async ({ record, itemType, parentUuid }) => {
  const axios = await createMenuManagementAxiosInstance();
  const { data } = await axios.get(
    `company/${record.companyId}/cuboh/merchant/${
      record.merchantId
    }/items/read_only/menu/0/${itemType}/${parentUuid || '0'}`,
  );
  return data;
};

export const validateMinMax = ({ values, form }) => {
  if (values.minimum > values.maximum) {
    form.setFields([
      {
        name: 'minimum',
        errors: ['Minimum must be smaller than the maximum.'],
      },
      {
        name: 'maximum',
        errors: ['Maximum must be larger than the minimum.'],
      },
    ]);
    throw new Error('Minimum must be smaller than Maximum');
  }
};

/** @param {{ companyId: *, uploadImageFile: *, itemType: *, imageEnhancedAt?: string }} params */
export const uploadImage = async ({ companyId, uploadImageFile, itemType, imageEnhancedAt }) => {
  const axios = await createMenuManagementAxiosInstance(IMAGE_CONTENT_REQUEST_TYPE);
  const data = new FormData();
  data.append('file', uploadImageFile.originFileObj);
  data.append('name', uploadImageFile.name);
  data.append('type', itemType);
  // Omitted entirely when the saved image was not AI-enhanced, so the column stays null.
  if (imageEnhancedAt) data.append('image_enhanced_at', imageEnhancedAt);
  const response = await axios.post(`/company/${companyId}/files`, data);
  return response?.data;
};

export const getTargetItemsFromMenu = async ({ companyId, merchantId, itemType, parentUuid }) => {
  const axios = await createMenuManagementAxiosInstance();
  const { data } = await axios.get(
    `company/${companyId}/merchant/${merchantId}/simple/children/${itemType}/${parentUuid}`,
  );
  return data;
};

export const getItemTaxRate = async ({ item }) => {
  const axios = await createMenuManagementAxiosInstance();
  const { data } = await axios.get(
    `/company/${item.companyId}/merchant/${item.merchantId}/item/${item.uuid}/tax`,
  );
  return data;
};

// Returns the mod group UUIDs that are not eligible to be assigned as a child of `item`
// (enforces 1-level nesting and prevents cycles). Mirrors getItemTaxRate's shape/timing.
export const getExcludedModGroups = async ({ item }) => {
  const axios = await createMenuManagementAxiosInstance();
  const { data } = await axios.get(
    `company/${item.companyId}/merchant/${item.merchantId}/item/${item.uuid}/excluded_mod_groups`,
  );
  return data;
};

export const getExcludedModifiers = async ({ item }) => {
  const axios = await createMenuManagementAxiosInstance();
  const { data } = await axios.get(
    `company/${item.companyId}/merchant/${item.merchantId}/excluded_modifiers`,
  );
  return data;
};

export const fetchItemsBySearchTerm = async ({ companyId, merchantId, menuId, searchTerm }) => {
  const axios = await createMenuManagementAxiosInstance();
  const response = await axios.get(
    `/company/${companyId}/cuboh/merchant/${merchantId}/menu/${menuId}/search/${searchTerm}`,
  );
  return response.data;
};

// eslint-disable-next-line consistent-return
export const dragAndDropReOrder = ({ menus, merchantId, from, to }) => {
  const menuToSort = menus[merchantId];
  const oldIndex = menuToSort.findIndex(menu => menu?.uuid === from.id);
  const newIndex = menuToSort.findIndex(menu => menu?.uuid === to.id);
  const fromMenuInfo = menuToSort[oldIndex];
  const toMenuInfo = menuToSort[newIndex];

  const reOrderedMenus = menuToSort
    .map((menu, currentIndex) => {
      // If menu keys match directly update new ordinal of active menu
      if (fromMenuInfo?.uuid === menu.uuid) {
        return { ...menu, ordinal: newIndex };
      }
      // If menu keys match directly update new ordinal of over menu
      if (toMenuInfo?.uuid === menu.uuid) {
        if (fromMenuInfo?.ordinal > toMenuInfo?.ordinal) {
          return { ...menu, ordinal: currentIndex + 1 };
        }
        return { ...menu, ordinal: currentIndex - 1 };
      }
      // If new index is less than any other array index then add one to those entries
      if (newIndex < currentIndex) {
        return { ...menu, ordinal: currentIndex + 1 };
      }
      return menu;
    })
    .filter(menu => menu)
    .sort((a, b) => a.ordinal - b.ordinal);

  return {
    ...menus,
    [merchantId]: reOrderedMenus,
  };
};

export const sortByItemTypeOrdinal = (data, itemType) => (a, b) =>
  data[itemType][a].ordinal - data[itemType][b].ordinal;

/** Pre-computes a flat lookup map from classificationsMap for O(1) access.
 * This should be called once (e.g., with useMemo in React) and the result
 * passed to renderClassificationIcons.
 * @param {Object} classificationsMap - The raw classifications map from the API
 * @returns {Object} A flat map where both keys and classification fields map to shortLabel */
export const createClassificationLookup = classificationsMap => {
  if (!classificationsMap) return null;

  const lookup = {};
  Object.entries(classificationsMap).forEach(([key, value]) => {
    if (value?.shortLabel) {
      lookup[key] = value.shortLabel;
      if (value.classification) {
        lookup[value.classification] = value.shortLabel;
      }
    }
  });
  return lookup;
};

export const renderClassificationIcons = (classifications, classificationsMap = null) => {
  if (!classifications?.length) {
    return 'Class: none';
  }

  const icons = classifications
    .filter(classification => !classification.isDeleted)
    .reduce((accumulator, { itemClass }) => {
      if (SKIP_ONLY_TAXES.includes(itemClass)) {
        return [...accumulator, '%'];
      }
      const shortLabel =
        classificationsMap?.[itemClass] || classificationFallbackShortLabel(itemClass);
      return shortLabel ? [...accumulator, shortLabel] : accumulator;
    }, []);

  return icons.length ? icons.join(', ') : 'Class: none';
};
